import React from 'react'
import moment from 'moment'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import BlockIcon from '@mui/icons-material/Block'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DownloadDoneIcon from '@mui/icons-material/DownloadDone'
import LinkIcon from '@mui/icons-material/Link'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import {
  describeExpiry,
  getShareCodeStatus,
  personName,
  SHARE_CODE_STATUS_META,
  summariseAttempts,
} from './shareCodeUtils'

export const codeFont = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontWeight: 800,
  letterSpacing: '0.12em',
}

const ShareCodeCard = ({ shareCode, onOpen, onCopy }) => {
  const status = getShareCodeStatus(shareCode)
  const meta = SHARE_CODE_STATUS_META[status]
  const { successful, failed } = summariseAttempts(shareCode)
  const creator = personName(shareCode?.createdBy)
  const isActive = status === 'active'

  return (
    <Card
      variant="outlined"
      onClick={onOpen}
      sx={{
        p: 1.5,
        borderRadius: 2.5,
        cursor: 'pointer',
        opacity: isActive ? 1 : 0.85,
        transition: 'border-color 120ms ease, box-shadow 120ms ease',
        '&:hover': {
          borderColor: 'anMainBlue.main',
          boxShadow: '0 6px 18px rgba(15,23,42,0.06)',
        },
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        gap={1.5}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
      >
        <Stack direction="row" gap={1.5} alignItems="center" minWidth={0}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: isActive
                ? 'rgba(0, 150, 76, 0.12)'
                : 'rgba(15, 23, 42, 0.06)',
              color: isActive ? 'anGreen.main' : 'text.secondary',
            }}
          >
            {isActive ? <LinkIcon /> : <LinkOffIcon />}
          </Box>
          <Box minWidth={0}>
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
              <Typography
                sx={{
                  ...codeFont,
                  fontSize: '1.15rem',
                  textDecoration: status === 'revoked' ? 'line-through' : 'none',
                }}
              >
                {shareCode.code}
              </Typography>
              <Chip
                size="small"
                label={meta.label}
                color={meta.color}
                variant={status === 'expired' ? 'outlined' : 'filled'}
                sx={{ borderRadius: 1, fontWeight: 700, height: 20 }}
              />
            </Stack>
            <Typography
              component="div"
              variant="caption"
              color="text.secondary"
              noWrap
            >
              {describeExpiry(shareCode)}
              {creator ? ` · Created by ${creator}` : ''}
              {shareCode.createdAt
                ? ` ${moment(shareCode.createdAt).fromNow()}`
                : ''}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" gap={0.5} alignItems="center" flexShrink={0}>
          <Chip
            size="small"
            variant="outlined"
            icon={<DownloadDoneIcon />}
            label={`${successful} download${successful === 1 ? '' : 's'}`}
            sx={{ borderRadius: 1 }}
          />
          {failed > 0 && (
            <Chip
              size="small"
              variant="outlined"
              color="warning"
              icon={<BlockIcon />}
              label={`${failed} refused`}
              sx={{ borderRadius: 1 }}
            />
          )}
          {isActive && (
            <Tooltip title="Copy code">
              <IconButton
                size="small"
                aria-label="Copy share code"
                onClick={(event) => {
                  event.stopPropagation()
                  onCopy()
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            size="small"
            aria-label="Share code details"
            onClick={(event) => {
              event.stopPropagation()
              onOpen()
            }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
    </Card>
  )
}

export default ShareCodeCard
